"use client";

import { useEffect, useRef, useState } from "react";
import { Droplets, ShieldCheck, Sparkles, Diamond } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Stat = {
  icon: typeof Droplets;
  label: string;
  ceramicValue: number;
  ceramicSuffix: string;
  ceramicLabel: string;
  waxLabel: string;
  metric: string;
};
const stats: Stat[] = [{
  icon: ShieldCheck,
  label: "Encryption",
  ceramicValue: 256,
  ceramicSuffix: "-bit",
  ceramicLabel: "AES Standard",
  waxLabel: "128-bit",
  metric: "Security"
}, {
  icon: Sparkles,
  label: "Uptime Rate",
  ceramicValue: 99,
  ceramicSuffix: "%",
  ceramicLabel: "Guaranteed",
  waxLabel: "95%",
  metric: "Reliability"
}, {
  icon: Diamond,
  label: "Server Speed",
  ceramicValue: 10,
  ceramicSuffix: "Gbps",
  ceramicLabel: "Ultra-fast",
  waxLabel: "1Gbps",
  metric: "Performance"
}, {
  icon: Droplets,
  label: "Data Privacy",
  ceramicValue: 100,
  ceramicSuffix: "%",
  ceramicLabel: "No-logs",
  waxLabel: "Partial",
  metric: "Compliance"
}];
function AnimatedNumber({
  target,
  suffix,
  start
}: {
  target: number;
  suffix: string;
  start: boolean;
}) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    const duration = 1600;
    const startTime = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target]);
  return <span className="font-serif tabular-nums bg-gradient-to-b from-foreground via-foreground/90 to-foreground/40 bg-clip-text text-transparent">
      {value}
      {suffix}
    </span>;
}
export default function Comparison() {
  const [sliderPos, setSliderPos] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const {
    ref: statsRef,
    isVisible: statsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: compareRef,
    isVisible: compareVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const updateSlider = (clientX: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = (clientX - rect.left) / rect.width * 100;
    setSliderPos(Math.max(0, Math.min(100, pct)));
  };
  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent | TouchEvent) => {
      const x = "touches" in e ? e.touches[0]?.clientX ?? 0 : (e as MouseEvent).clientX;
      updateSlider(x);
    };
    const onUp = () => setIsDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [isDragging]);
  const startDrag = () => {
    setIsDragging(true);
    setHasInteracted(true);
  };
  return <section data-section-id="2694" id="compliancestandardscomparison" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 px-4 md:px-8">
      <div className="pointer-events-none absolute inset-0" style={{
      background: "radial-gradient(ellipse at 50% 30%, hsl(220 60% 20% / 0.35), transparent 60%)"
    }} />
      <div className="pointer-events-none absolute inset-0" style={{
      background: "radial-gradient(ellipse at 50% 100%, hsl(200 80% 30% / 0.15), transparent 70%)"
    }} />

      <div className="relative mx-auto max-w-7xl">
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs md:text-sm tracking-[0.3em] uppercase text-muted-foreground mb-6">
            — The Standard —
          </p>
          <h2 className="font-serif font-default text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
            Basic VPNs fail.{" "}
            <span className="italic bg-gradient-to-b from-foreground via-foreground/80 to-foreground/30 bg-clip-text text-transparent">
              VPNGENIX secures.
            </span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            Drag the slider to see the difference between standard public networks
            and the enterprise-grade security of VPNGENIX.
          </p>
        </div>

        <div ref={compareRef} className={`mt-16 md:mt-20 transition-all duration-1000 ease-out ${compareVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div ref={containerRef} className="relative w-full aspect-[16/9] overflow-hidden rounded-2xl border border-border shadow-2xl select-none" style={{
          cursor: isDragging ? "ew-resize" : "default"
        }}>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/public_network_vulnerability.png" alt="Public network showing data exposure and risks" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" draggable={false} />

            <div className="absolute inset-0 overflow-hidden" style={{
            clipPath: `inset(0 0 0 ${sliderPos}%)`
          }}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpngenix_secure_tunnel.webp" alt="VPNGENIX secure encrypted tunnel visualization" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
            </div>

            <div className="absolute top-4 left-4 md:top-6 md:left-6 max-w-[45%]">
              <div className="inline-flex flex-col bg-background/70 backdrop-blur-md border border-border rounded-lg px-3 py-2 md:px-4 md:py-3">
                <span className="font-mono text-[10px] md:text-xs tracking-[0.2em] uppercase text-muted-foreground">
                  Public Wi-Fi
                </span>
                <span className="font-default text-xs md:text-sm text-foreground/90 mt-1">
                  High risk exposure
                </span>
              </div>
            </div>
            <div className="absolute top-4 right-4 md:top-6 md:right-6 max-w-[45%] text-right">
              <div className="inline-flex flex-col bg-background/70 backdrop-blur-md border border-border rounded-lg px-3 py-2 md:px-4 md:py-3">
                <span className="font-mono text-[10px] md:text-xs tracking-[0.2em] uppercase bg-gradient-to-r from-foreground via-foreground/80 to-foreground/50 bg-clip-text text-transparent">
                  VPNGENIX
                </span>
                <span className="font-default text-xs md:text-sm text-foreground/90 mt-1">
                  Military-grade
                </span>
              </div>
            </div>

            <div className="absolute top-0 bottom-0 w-px pointer-events-none" style={{
            left: `${sliderPos}%`,
            background: "linear-gradient(to bottom, hsl(40 15% 95% / 0.3), hsl(40 15% 95% / 0.9), hsl(40 15% 95% / 0.3))",
            boxShadow: "0 0 20px hsl(40 15% 95% / 0.4)"
          }} />

            <div className="absolute top-1/2 z-10 flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-lg border-2 border-foreground/80" style={{
            left: `${sliderPos}%`,
            transform: "translate(-50%, -50%)",
            background: "linear-gradient(135deg, hsl(40 15% 98%), hsl(220 8% 60%), hsl(40 15% 98%))",
            boxShadow: "0 0 0 4px hsl(220 15% 6% / 0.5), 0 8px 24px hsl(0 0% 0% / 0.5)",
            cursor: "ew-resize"
          }} onMouseDown={startDrag} onTouchStart={startDrag} role="slider" aria-label="Drag to compare" tabIndex={0}>
              {!hasInteracted && <span className="absolute inset-0 rounded-lg animate-ping" style={{
              background: "hsl(40 15% 95% / 0.4)"
            }} />}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="relative text-background">
                <path d="M5 4 L2 8 L5 12 M11 4 L14 8 L11 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
        </div>

        <div ref={statsRef} className="mt-16 md:mt-24 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border rounded-2xl overflow-hidden border border-border">
          {stats.map((stat, i) => {
          const Icon = stat.icon;
          return <div key={stat.label} data-index={i} className="bg-background p-6 md:p-8 flex flex-col gap-4 transition-colors hover:bg-card" style={{
            transitionDelay: `${i * 80}ms`
          }}>
                <div className="flex items-center justify-between">
                  <Icon className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                    {stat.metric}
                  </span>
                </div>

                <div className="flex items-baseline gap-1">
                  <span className="text-5xl md:text-6xl font-default font-light leading-none">
                    <AnimatedNumber target={stat.ceramicValue} suffix={stat.ceramicSuffix} start={statsVisible} />
                  </span>
                </div>

                <div>
                  <h3 className="font-default font-semibold text-lg md:text-xl text-foreground">
                    {stat.label}
                  </h3>
                  <div className="mt-3 space-y-1.5 font-mono text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground tracking-wider uppercase">
                        VPNGENIX
                      </span>
                      <span className="text-foreground">
                        {stat.ceramicLabel}
                      </span>
                    </div>
                    <div className="flex items-center justify-between opacity-60">
                      <span className="text-muted-foreground tracking-wider uppercase">
                        Others
                      </span>
                      <span className="text-muted-foreground line-through">
                        {stat.waxLabel}
                      </span>
                    </div>
                  </div>
                </div>
              </div>;
        })}
        </div>

        <div className="mt-16 md:mt-20 flex flex-col items-center text-center">
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-muted-foreground mb-6">
            Ready for total privacy?
          </p>
          <Button asChild size="lg" className="font-default tracking-wide px-8 py-6 text-base rounded-lg">
            <Link to="https://vpngenix.com/go/NordVPN" newTab>Get VPNGENIX Now</Link>
          </Button>
          <p className="mt-5 text-sm text-muted-foreground max-w-md">
            Global server network. Zero-log policy. Total digital freedom.
          </p>
        </div>
      </div>
    </section>;
}